import moment from 'moment-timezone';
import { getPath } from 'utils';
import {
  CimaMipApplicationProgressRoutesMap,
  CimaMipApplicationFormList,
  CimaMipMainApplicationSubHeading,
  CimaMipMainApplicationFormInstructions,
  CimaMipMainApplicationHeading,
} from 'constants/index';

export const dataChecker = (items?: any) => {
  return items !== undefined && items !== '' && items !== null;
};

export const dataConvertion = (items: any) => {
  return items !== undefined && items !== '' && items !== null ? items : undefined;
};

export const dataConvertionCheckbox = (item: any) => {
  return typeof item === 'string' ? (item === 'true' ? true : false) : item;
};

export const checkIfMipRenewalToday = (
  accessEndDate?: any,
  renewalSeasonStartDate?: any,
  renewalSeasonEndDate?: any
) => {
  const mipCredentialAccessEndYear = new Date(accessEndDate).getFullYear();
  const today = moment();

  const mipRenewalSeasonStart = moment(renewalSeasonStartDate, 'MM-DD');
  const mipRenewalSeasonEnd = moment(renewalSeasonEndDate, 'MM-DD');

  const mipRenewalSeasonStartMonthDay = moment(renewalSeasonStartDate).format('MM-DD');
  const mipRenewalSeasonEndMonthDay = moment(renewalSeasonEndDate).format('MM-DD');

  const mipRenewalStartDate = mipRenewalSeasonStart.isAfter(mipRenewalSeasonEnd)
    ? moment(`${mipCredentialAccessEndYear}-${mipRenewalSeasonStartMonthDay}`)
    : moment(`${mipCredentialAccessEndYear - 1}-${mipRenewalSeasonStartMonthDay}`);
  const mipRenewalEndDate = moment(`${mipCredentialAccessEndYear}-${mipRenewalSeasonEndMonthDay}`);
  const isMipRenewalToday = today.isBetween(mipRenewalStartDate, mipRenewalEndDate);

  return isMipRenewalToday;
};

interface MipAmlCompletionState {
  isCompleteAml?: boolean | string | null;
  amlLastModifiedDate?: string | null;
  isRenewalAmlSubmitted?: boolean;
}

/**
 * Determines whether the resolved AML annual return is stale for the current MiP renewal
 * cycle, i.e. it was last modified before the renewal season started (`mipStartDate`) or
 * has no usable last-modified date. Mirrors the form-reset logic in `PageMipAmlApplication`
 * so the completion gate and the form display always stay in sync.
 */
export const isMipAmlStaleForRenewal = (
  isMipRenewalSeason?: boolean,
  amlLastModifiedDate?: string | null,
  mipStartDate?: string | null
): boolean => {
  if (!isMipRenewalSeason) {
    return false;
  }
  const latestSubmissionDate = new Date(amlLastModifiedDate ?? 'invalid');
  const seasonStartDate = new Date(mipStartDate ?? 'invalid');
  const isSubmissionBeforeSeasonStart =
    Number.isNaN(latestSubmissionDate.getTime()) || Number.isNaN(seasonStartDate.getTime())
      ? null
      : latestSubmissionDate.getTime() < seasonStartDate.getTime();
  return Boolean(isSubmissionBeforeSeasonStart || Number.isNaN(latestSubmissionDate.getTime()) || !amlLastModifiedDate);
};

/**
 * Cycle-aware AML completion check. During MiP renewal season the persisted
 * `SFS_AML_AMLComplete__c` flag (`aml.isCompleteAml`) must NOT satisfy the gate: it can be carried
 * forward from a prior year, or set by enrollment/back-office/clone on a record the member never
 * submitted for this cycle (both record dates then fall inside the cycle, so a date check cannot
 * catch it). Completion is therefore driven by `aml.isRenewalAmlSubmitted`, set only when the member
 * submits a fresh AML annual return in the current renewal session and reset on renewal entry.
 * Outside renewal season the new-member enrollment flow is unchanged.
 */
export const isMipAmlCompleteForCycle = (
  isMipRenewalSeason: boolean | undefined,
  aml: MipAmlCompletionState | null | undefined
): boolean => {
  if (isMipRenewalSeason) {
    return Boolean(aml?.isRenewalAmlSubmitted);
  }
  return Boolean(aml?.isCompleteAml);
};

export const isSubmitButton = (
  isMipRenewalSeason: any,
  hasMipCredential: any,
  mipData: any,
  aml: any,
  workHistory: any
) => {
  const isCommonComplete =
    !mipData?.contactDetails?.practiceList?.[0]?.isCompleteContact ||
    !mipData?.practiceRequirement?.isCompletePracticeRequirement ||
    !isMipAmlCompleteForCycle(isMipRenewalSeason, aml);

  return isMipRenewalSeason && hasMipCredential
    ? isCommonComplete
    : isCommonComplete || !workHistory?.isWorkHistoryComplete;
};

export const cardRenewOrNot = (isMipRenewalSeason: any, hasMipCredential: any, formCard: any) => {
  return isMipRenewalSeason && hasMipCredential
    ? formCard.filter((f: any) => f.cardHeading !== CimaMipApplicationFormList.WORK_HISTORY)
    : formCard;
};

export const isFileItemTrue = (fileItem: any) => {
  return fileItem.isComplete === true || fileItem.isComplete === 'true';
};

export const isFileItemFalse = (fileItem: any) => {
  return fileItem.isComplete === false || fileItem.isComplete === 'false' || !fileItem.isComplete;
};

export const getPathApplicationProgress = (application: any) => {
  return getPath(CimaMipApplicationProgressRoutesMap[application.applicationProgress || '']);
};

export const formHeader = (isMipRenewalSeason: any, hasMipCredential: any) => {
  return isMipRenewalSeason && hasMipCredential
    ? CimaMipMainApplicationHeading.MIP_HEADING_RENEWAL
    : CimaMipMainApplicationHeading.MIP_HEADING_NEW;
};

export const formSubHeader = (isMipRenewalSeason: any, hasMipCredential: any) => {
  return isMipRenewalSeason && hasMipCredential
    ? CimaMipMainApplicationSubHeading.MIP_SUBHEADING_RENEWAL
    : CimaMipMainApplicationSubHeading.MIP_SUBHEADING_NEW;
};

export const formInstruction = (isMipRenewalSeason: any, hasMipCredential: any) => {
  return isMipRenewalSeason && hasMipCredential
    ? CimaMipMainApplicationFormInstructions.MIP_FORM_INSTRUCTIONS_RENEWAL
    : CimaMipMainApplicationFormInstructions.MIP_FORM_INSTRUCTIONS_NEW;
};

export const formCardComplete = (isMipRenewalSeason: any, hasMipCredential: any, formCard: any) => {
  return !(isMipRenewalSeason && hasMipCredential) ? formCard.length : formCard.length - 1;
};

export const submitText = (checked: any) => {
  return checked.length === 4
    ? 'Please submit application'
    : 'Please complete each of the sections  to submit the application';
};
